import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { MdEdit, MdDelete } from "react-icons/md";
import { useApi } from "../../api/useApi";
import { businessRuleStandardCitations } from "../../api/ruleQueries";
import {
  createPolicyCitation,
  updatePolicyCitation,
  deletePolicyCitation,
} from "../../api/ruleMutations";
import { StyledInput, FormActions } from "../Form/FormControls";
import Button from "../Button";
import Spinner from "../Loaders/Spinner";
import ErrorMessages from "../Notifications/ErrorMessages";
import DataTable from "../Table/DataTable";
import RowActionsMenu, { MenuItem } from "../Table/RowActionsMenu";
import CitationLink from "./CitationLink";
import {
  TableWrap,
  EditFields,
  ReadOnlyTag,
  ActionButtons,
} from "./citationStyles";
import { ToastContainer, toast } from "react-toastify";
import {
  citationIsValid,
  isAcesCitation,
  sortCitations,
} from "./citationHelpers";

// Edit-path citation editor. Loads the policy's citations into a DataTable: the
// Citation column deep-links each title, and the Actions column carries a kebab
// (3-dots) menu with Edit/Remove for client citations. Edit expands the row's
// Citation cell into the two-field editor with a per-row Save. ACES managed
// citations are read-only (no actions); the server enforces that ACES rows cannot
// be modified.
const UpdateCitations = ({ remoteObjectId }) => {
  const standardId = Number(remoteObjectId);

  const [{ loading, errors, data }, fetchLatest] = useApi(
    businessRuleStandardCitations,
    { standardId }
  );

  const [{ errors: mutationErrors, data: mutationData }, runMutation] = useApi();

  const [clientRows, setClientRows] = useState([]);
  const [newRows, setNewRows] = useState([]);
  const [editingIds, setEditingIds] = useState(new Set());
  const [isSaving, setIsSaving] = useState(false);
  // Stable client-side keys for staged new rows, plus the key of the row currently
  // being created, so a successful create removes only that row (not other in-progress ones).
  const nextNewRowKey = useRef(0);
  const [pendingNewKey, setPendingNewKey] = useState(null);
  // Id of the row whose update is in flight, so only that row collapses on success.
  const [pendingSaveId, setPendingSaveId] = useState(null);

  // Latest server citations, read by cancelEditing without making that callback
  // depend on `data` (which would rebuild the memoized columns and remount the
  // in-cell inputs, dropping focus).
  const serverCitationsRef = useRef([]);
  useEffect(() => {
    serverCitationsRef.current = data?.businessRuleStandardById?.citations ?? [];
  }, [data]);

  // Stable table data keyed off the server payload only, so the in-memory row
  // rebuild happens on refetch — not on every keystroke while a row is being
  // edited (the edit values live in clientRows, not here).
  const citations = useMemo(
    () => sortCitations(data?.businessRuleStandardById?.citations ?? []),
    [data]
  );

  useEffect(() => {
    const all = data?.businessRuleStandardById?.citations ?? [];
    // Rebuild from the server, but preserve unsaved input in rows the user is still
    // editing. A refetch fires after every save/delete, so blindly resetting here would
    // silently discard in-progress edits in sibling rows. Collapsed rows always take the
    // server value.
    setClientRows((prev) => {
      const prevById = new Map(prev.map((r) => [r.id, r]));
      return all
        .filter((c) => !isAcesCitation(c))
        .map((c) => {
          const existing = prevById.get(c.id);
          return existing && editingIds.has(c.id)
            ? { id: c.id, title: existing.title, url: existing.url }
            : { id: c.id, title: c.title ?? "", url: c.url ?? "" };
        });
    });
  }, [data, editingIds]);

  // Refetch + toast after a successful mutation.
  useEffect(() => {
    if (mutationData && !mutationErrors?.length) {
      setIsSaving(false);
      // Only drop the new row that was just created; leave any other in-progress
      // new rows (and their unsaved input) intact. Update/delete leave newRows alone.
      if (pendingNewKey !== null) {
        setNewRows((prev) => prev.filter((r) => r.key !== pendingNewKey));
        setPendingNewKey(null);
      }
      // Collapse only the row whose update just succeeded.
      if (pendingSaveId !== null) {
        setEditingIds((prev) => {
          const next = new Set(prev);
          next.delete(pendingSaveId);
          return next;
        });
        setPendingSaveId(null);
      }
      toast.info("Citations saved", {
        position: "top-right",
        autoClose: 2000,
      });
      fetchLatest({ query: businessRuleStandardCitations, variables: { standardId } });
    }
  }, [mutationData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (mutationErrors?.length) {
      setIsSaving(false);
      // A failed create must not leave a stale pending key behind, or a later
      // successful update/delete would wrongly drop the still-unsaved new row.
      // Likewise a failed update must not collapse its row on the next success.
      setPendingNewKey(null);
      setPendingSaveId(null);
    }
  }, [mutationErrors]);

  // All row callbacks are useCallback-stable (functional setState + the stable
  // useApi run fn) so the memoized `columns` below keep a constant identity. A new
  // Cell identity per render is what remounts a react-table cell and drops input
  // focus after a single keystroke.
  const updateClientRow = useCallback(
    (id, field, value) =>
      setClientRows((prev) =>
        prev.map((r) => (r.id === id ? { ...r, [field]: value } : r))
      ),
    []
  );

  const startEditing = useCallback(
    (id) => setEditingIds((prev) => new Set(prev).add(id)),
    []
  );

  const cancelEditing = useCallback((id) => {
    const server = serverCitationsRef.current.find((c) => c.id === id);
    setClientRows((prev) =>
      prev.map((r) =>
        r.id === id
          ? { id, title: server?.title ?? "", url: server?.url ?? "" }
          : r
      )
    );
    setEditingIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const saveExisting = useCallback(
    (row) => {
      setIsSaving(true);
      // Not a create; clear any pending key so the success handler doesn't drop a new row.
      setPendingNewKey(null);
      setPendingSaveId(row.id);
      runMutation({
        query: updatePolicyCitation,
        variables: { citationId: row.id, title: row.title.trim(), url: row.url.trim() },
      });
    },
    [runMutation]
  );

  const deleteExisting = useCallback(
    (id) => {
      setIsSaving(true);
      setPendingNewKey(null);
      setPendingSaveId(null);
      runMutation({ query: deletePolicyCitation, variables: { citationId: id } });
    },
    [runMutation]
  );

  // Brand-new (uncommitted) rows render as editing rows inside the table, so a
  // new citation looks the same as an edited one. All handlers are useCallback-
  // stable to keep the memoized columns (and thus input focus) constant.
  const updateNewRow = useCallback(
    (key, field, value) =>
      setNewRows((prev) =>
        prev.map((r) => (r.key === key ? { ...r, [field]: value } : r))
      ),
    []
  );

  const addNewRow = useCallback(
    () =>
      setNewRows((prev) => [
        ...prev,
        { key: nextNewRowKey.current++, title: "", url: "" },
      ]),
    []
  );

  const removeNewRow = useCallback(
    (key) => setNewRows((prev) => prev.filter((r) => r.key !== key)),
    []
  );

  const createNew = useCallback(
    (row) => {
      setIsSaving(true);
      setPendingNewKey(row.key);
      setPendingSaveId(null);
      runMutation({
        query: createPolicyCitation,
        variables: {
          policyId: standardId,
          title: row.title.trim(),
          url: row.url.trim(),
        },
      });
    },
    [runMutation, standardId]
  );

  // Flatten every field the cells display into the row data (including editing
  // state and the live edit values), so the cells read from `original` instead of
  // a closure. That keeps the Cell identities stable while the values change.
  // Server citations first, then any brand-new rows still being added.
  const tableRows = [
    ...citations.map((c) => {
      if (isAcesCitation(c)) {
        return {
          id: c.id,
          isNew: false,
          aces: true,
          editing: false,
          title: c.title ?? "",
          url: c.url ?? "",
          isSaving,
        };
      }
      const editing = editingIds.has(c.id);
      const draft = editing ? clientRows.find((r) => r.id === c.id) : null;
      return {
        id: c.id,
        isNew: false,
        aces: false,
        editing,
        title: draft ? draft.title : c.title ?? "",
        url: draft ? draft.url : c.url ?? "",
        isSaving,
      };
    }),
    ...newRows.map((r) => ({
      key: r.key,
      isNew: true,
      aces: false,
      editing: true,
      title: r.title,
      url: r.url,
      isSaving,
    })),
  ];

  const columns = useMemo(
    () => [
      {
        Header: "Citation",
        id: "citation",
        disableSortBy: true,
        Cell: ({ row: { original } }) => {
          if (original.aces || !original.editing) {
            return <CitationLink title={original.title} url={original.url} />;
          }
          const onField = (field) => (e) =>
            original.isNew
              ? updateNewRow(original.key, field, e.target.value)
              : updateClientRow(original.id, field, e.target.value);
          return (
            <EditFields>
              <StyledInput
                label="Citation Name"
                placeholder="Citation label"
                value={original.title}
                onChange={onField("title")}
              />
              <StyledInput
                label="URL"
                placeholder="https://..."
                value={original.url}
                onChange={onField("url")}
              />
            </EditFields>
          );
        },
      },
      {
        Header: "Actions",
        id: "actions",
        align: "right",
        disableSortBy: true,
        Cell: ({ row: { original } }) => {
          if (original.aces) {
            // ACES managed citations are read-only; no edit/remove affordances.
            return (
              <div style={{ textAlign: "right" }}>
                <ReadOnlyTag title="ACES managed citations are read-only">
                  ACES Managed
                </ReadOnlyTag>
              </div>
            );
          }
          if (original.editing) {
            return (
              <ActionButtons>
                <Button
                  type="button"
                  size="small"
                  disabled={original.isSaving || !citationIsValid(original)}
                  onClick={() =>
                    original.isNew
                      ? createNew({
                          key: original.key,
                          title: original.title,
                          url: original.url,
                        })
                      : saveExisting({
                          id: original.id,
                          title: original.title,
                          url: original.url,
                        })
                  }
                >
                  {original.isNew ? "Create" : "Save"}
                </Button>
                <Button
                  type="button"
                  size="small"
                  disabled={original.isSaving}
                  onClick={() =>
                    original.isNew
                      ? removeNewRow(original.key)
                      : cancelEditing(original.id)
                  }
                >
                  Cancel
                </Button>
              </ActionButtons>
            );
          }
          return (
            <div style={{ textAlign: "right" }}>
              <RowActionsMenu ariaLabel="Citation actions">
                <MenuItem
                  type="button"
                  disabled={original.isSaving}
                  onClick={() => startEditing(original.id)}
                >
                  <MdEdit /> Edit
                </MenuItem>
                <MenuItem
                  danger
                  type="button"
                  disabled={original.isSaving}
                  onClick={() => deleteExisting(original.id)}
                >
                  <MdDelete /> Remove
                </MenuItem>
              </RowActionsMenu>
            </div>
          );
        },
      },
    ],
    [
      updateClientRow,
      updateNewRow,
      startEditing,
      cancelEditing,
      saveExisting,
      deleteExisting,
      createNew,
      removeNewRow,
    ]
  );

  return (
    <>
      <h3>Citations</h3>

      {tableRows.length ? (
        <TableWrap>
          <DataTable
            mode="clientSide"
            hidePagination
            disableSortBy
            noMargin
            data={tableRows}
            columns={columns}
          />
        </TableWrap>
      ) : (
        <p>No citations yet.</p>
      )}

      {errors || mutationErrors ? (
        <ErrorMessages errors={errors || mutationErrors} />
      ) : null}

      <FormActions>
        <Button
          type="button"
          disabled={loading || isSaving}
          onClick={addNewRow}
        >
          {loading || isSaving ? <Spinner /> : "Add Citation"}
        </Button>
      </FormActions>

      <ToastContainer />
    </>
  );
};

export default UpdateCitations;
